<template>
  <JHeader />

  <main class="mx-auto max-w-6xl px-6 md:px-12">
    <!-- Hero -->
    <section class="flex flex-col pt-14 pb-16 md:pt-24 md:pb-24">
      <p
        class="hero-enter font-source-code text-turquoise text-base md:text-lg"
        style="animation-delay: 0.1s"
      >
        Hi, my name is
      </p>
      <h1
        class="hero-enter mt-4 font-outfit font-extrabold text-paleBlue text-5xl md:text-6xl lg:text-7xl"
        style="animation-delay: 0.25s"
      >
        João Gabriel.
      </h1>
      <p
        class="hero-enter mt-2 font-outfit font-semibold text-turquoise text-3xl md:text-5xl lg:text-6xl"
        style="animation-delay: 0.4s"
      >
        I build things for the web.
      </p>
      <p
        class="hero-enter mt-8 max-w-xl font-outfit text-lg text-white/70"
        style="animation-delay: 0.55s"
      >
        I'm a Staff Full Stack Engineer with more than 7 years of experience
        turning complex problems into fast and reliable products. These days I
        build commerce experiences in the Shopify ecosystem.
      </p>
      <p
        class="hero-enter mt-6 font-source-code text-sm text-white/60"
        style="animation-delay: 0.7s"
      >
        <span class="text-turquoise">$</span> currently working as Staff
        Software Engineer @ Abra Promotions<span class="cursor-blink"></span>
      </p>
      <div
        class="hero-enter mt-12 flex flex-wrap items-center gap-6"
        style="animation-delay: 0.85s"
      >
        <a
          href="#experience"
          class="rounded border-2 border-turquoise px-6 py-3 font-source-code text-sm text-turquoise transition hover:-translate-y-0.5 hover:bg-turquoise/10"
          @click.prevent="scrollToSection('#experience')"
        >
          View my experience
        </a>
        <a
          href="#contact"
          class="font-source-code text-sm text-paleBlue transition hover:text-turquoise"
          @click.prevent="scrollToSection('#contact')"
        >
          Get in touch →
        </a>
      </div>
    </section>

    <!-- About -->
    <section id="about" class="scroll-mt-24 py-16 md:py-20" v-reveal>
      <div class="flex items-center gap-6">
        <h2
          class="whitespace-nowrap font-outfit font-extrabold text-paleBlue text-3xl md:text-4xl"
        >
          About me
        </h2>
        <span class="h-px flex-1 bg-gunMetal" aria-hidden="true"></span>
      </div>

      <div
        class="mt-12 grid grid-cols-1 gap-16 lg:grid-cols-[3fr_2fr] lg:gap-20"
      >
        <div>
          <p class="font-outfit text-lg text-white/80">
            I'm a Staff Full Stack Engineer with over 7 years of experience
            building and scaling web applications. I lead solution design,
            define technical standards and mentor engineers while staying hands
            on across the whole stack, from database design and APIs to
            polished, accessible UIs. Lately I've been deep in the Shopify
            ecosystem building commerce experiences with Node.js, GraphQL and
            Next.js.
          </p>
          <p class="mt-4 font-outfit text-lg text-white/80">
            Here are a few technologies I've been working with recently:
          </p>
          <ul class="mt-6 grid grid-cols-2 gap-x-4 gap-y-2 md:grid-cols-3">
            <li
              v-for="skill in skills"
              :key="skill"
              class="flex items-center gap-2 font-source-code text-sm text-white/80"
            >
              <span class="text-turquoise" aria-hidden="true">▹</span>
              {{ skill }}
            </li>
          </ul>
        </div>

        <div class="group relative mx-auto w-64 md:w-72 lg:mt-2 lg:w-full">
          <div
            class="absolute inset-0 translate-x-4 translate-y-4 rounded border-2 border-turquoise transition-transform duration-300 group-hover:translate-x-2 group-hover:translate-y-2"
            aria-hidden="true"
          ></div>
          <div class="relative overflow-hidden rounded">
            <img
              :src="profileImage"
              alt="Portrait of João Gabriel"
              class="aspect-[4/5] w-full object-cover"
            />
            <div
              class="absolute inset-0 bg-turquoise/20 transition-colors duration-300 group-hover:bg-transparent"
              aria-hidden="true"
            ></div>
          </div>
        </div>
      </div>
    </section>

    <!-- Experience -->
    <section id="experience" class="scroll-mt-24 py-16 md:py-20" v-reveal>
      <div class="flex items-center gap-6">
        <h2
          class="whitespace-nowrap font-outfit font-extrabold text-paleBlue text-3xl md:text-4xl"
        >
          Experience
        </h2>
        <span class="h-px flex-1 bg-gunMetal" aria-hidden="true"></span>
      </div>

      <JExperience />
    </section>

    <!-- Contact -->
    <section
      id="contact"
      class="mx-auto max-w-2xl scroll-mt-24 py-16 text-center md:py-24"
      v-reveal
    >
      <p class="font-source-code text-turquoise">// what's next?</p>
      <h2
        class="mt-4 font-outfit font-extrabold text-paleBlue text-4xl md:text-5xl"
      >
        Get in touch
      </h2>
      <p class="mt-6 font-outfit text-lg text-white/70">
        Whether you have an opportunity, a question or just want to say hi, my
        inbox is always open.
      </p>

      <div
        class="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row sm:gap-8"
      >
        <a
          href="mailto:joao@joao-global-software-development.dev"
          class="inline-flex w-44 items-center justify-center rounded border-2 border-turquoise px-4 py-2 font-source-code text-base text-paleBlue transition hover:-translate-y-0.5 hover:bg-turquoise/10 sm:w-auto"
        >
          <svg
            class="-ml-1 mr-3 h-6 w-6"
            viewBox="0 0 21 17"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M19.0312 0.625C20.1182 0.625 21 1.50643 21 2.59375C21 3.21309 20.7088 3.79551 20.2125 4.16875L11.2875 10.8625C10.8199 11.2111 10.1801 11.2111 9.7125 10.8625L0.7875 4.16875C0.291744 3.79551 0 3.21309 0 2.59375C0 1.50643 0.881426 0.625 1.96875 0.625H19.0312ZM8.925 11.9125C9.86016 12.6139 11.1398 12.6139 12.075 11.9125L21 5.21875V13.75C21 15.1979 19.8229 16.375 18.375 16.375H2.625C1.1751 16.375 0 15.1979 0 13.75V5.21875L8.925 11.9125Z"
              fill="#41E4BD"
            />
          </svg>
          E-mail
        </a>
        <a
          href="https://www.linkedin.com/in/joao-global-development/"
          target="_blank"
          rel="noopener"
          class="inline-flex w-44 items-center justify-center rounded border-2 border-turquoise px-4 py-2 font-source-code text-base text-paleBlue transition hover:-translate-y-0.5 hover:bg-turquoise/10 sm:w-auto"
        >
          <svg
            class="-ml-1 mr-3 h-6 w-6"
            viewBox="0 0 15 19"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M3.39296 18.3751H0.433171V6.1073H3.39296V18.3751ZM1.91147 4.43387C0.965028 4.43387 0.197357 3.42488 0.197357 2.20672C0.197357 1.62159 0.377951 1.06043 0.69941 0.646678C1.02087 0.23293 1.45686 0.000488281 1.91147 0.000488281C2.36608 0.000488281 2.80207 0.23293 3.12353 0.646678C3.44499 1.06043 3.62559 1.62159 3.62559 2.20672C3.62559 3.42488 2.8576 4.43387 1.91147 4.43387ZM14.4705 18.3751H11.5171V12.4032C11.5171 10.98 11.4948 9.15477 9.97823 9.15477C8.43938 9.15477 8.20357 10.7011 8.20357 12.3007V18.3751H5.24697V6.1073H8.08566V7.78074H8.12709C8.52223 6.81687 9.48748 5.79969 10.9275 5.79969C13.923 5.79969 14.4737 8.33855 14.4737 11.6362V18.3751H14.4705Z"
              fill="#41E4BD"
            />
          </svg>
          LinkedIn
        </a>
      </div>
    </section>
  </main>

  <footer class="pb-10 text-center">
    <p class="font-source-code text-xs text-white/50">
      Designed &amp; built by João Gabriel
    </p>
    <p class="mt-2 px-6 font-source-code text-xs text-white/40">
      @2026 Joao Global Software Development LLC | 30 N Gould St Ste R,
      Sheridan, WY 82801, USA | +1 (628) 201-7303
    </p>
  </footer>
</template>
<script setup>
import JHeader from "@/components/JHeader.vue";

import profileImage from "./assets/profile.jpeg";
import JExperience from "@/components/JExperience.vue";
import { scrollToSection } from "@/utils/smoothScroll";

const skills = [
  "Node.js",
  "TypeScript",
  "React / Next.js",
  "GraphQL",
  "Vue / Nuxt",
  "PostgreSQL",
  "Shopify",
  "Docker",
  "Laravel",
];
</script>
